import { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Phone, CalendarClock, Image as ImageIcon, Video } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCall } from '@/contexts/call-context';
import { audioNotifier } from '@/lib/audio-notifier';
import { SoundSelector } from '@/components/sound-selector';
import { LatestPreviews, PreviewProjectThumb as Thumb, type PreviewShareRow } from '@/components/latest-previews';
import { useAuth } from '@/contexts/auth-context';
import { CalendarUrgentList, calendarAgendaRange, ScheduledCalendarPanel, ScheduledViewAsChips } from '@/components/calendar/ScheduledCalendarPanel';
import { formatCalendarCallCountdown, isUpcomingCalendarCall, isPriorityCalendarCall, useCalendarAgenda, useCalendarCallActions, useCalendarClock } from '@/hooks/use-calendar';
import { CALL_COLORS } from '@shared/call-participant-tone';
import { apiRequest } from '@/lib/queryClient';

/**
 * Header activity bar (task #77 follow-on): lives in the empty band
 * between the notification/filter row and the main table. Three
 * expandable sections:
 *  1. Active calls — every project-assigned team member (admins: all)
 *     can join/rejoin a running client or internal call.
 *  2. Scheduled calls — upcoming calendar entries and callback requests.
 *  3. Latest previews — most recent preview share pages.
 * Calls come from /api/activity-bar and poll lightly. Preview history uses
 * its own on-open, identity-scoped paginated query (never the call poll).
 */

type ActiveCall = {
  sessionId: string;
  kind: 'client' | 'internal';
  hasExternalParticipants?: boolean;
  status: string;
  startedAt: string;
  projectId: string | null;
  projectName: string | null;
  thumbnail: string | null;
  shareToken: string | null;
  /** Current joined team members and external guests (not invitees/history). */
  participants: string[];
  /** Server-authoritative: the requesting user is initiator/invitee. */
  personal: boolean;
  /** The requesting user's own participant row ('invited' = this call is ringing FOR them). */
  myStatus?: 'invited' | 'active' | 'left' | 'declined' | string | null;
  /** First name of whoever started the call (also while still ringing out). */
  initiatorName?: string | null;
};

/** The call is ringing for the current user: they are an invitee who has
 * not answered yet. The row must say so explicitly ("Ben is calling you")
 * — a generic "ringing" line left recipients unsure the call was for them. */
const isRingingForMe = (c: ActiveCall) => c.status === 'pending' && c.myStatus === 'invited';

type RecentCall = {
  sessionId: string;
  kind: 'client' | 'internal';
  hasExternalParticipants?: boolean;
  startedAt: string;
  endedAt: string | null;
  durationSec: number | null;
  projectId: string | null;
  projectName: string | null;
  thumbnail: string | null;
  participants: string[];
  /** Existing project call pulse, if one was already generated. */
  callPulseId: number | null;
  /** Server-authoritative admin/connected-participant recovery gate. */
  canCreateCallPulse: boolean;
  /** Historical recovery is a manual, non-recorded project record. */
  isHistoricalCallPulseRecovery?: boolean;
};

// `kind` selects the join transport; mixed calls may still use internal joining.
const isExternalCall = (call: { kind: 'client' | 'internal'; hasExternalParticipants?: boolean }) =>
  call.hasExternalParticipants === true || call.kind === 'client';

function formatDuration(sec: number | null): string {
  if (sec == null) return '';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function daysAgo(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

export function Section({
  title,
  icon,
  tone,
  expanded,
  count,
  live,
  liveIds,
  testId,
  openSignal,
  onOpenChange,
  collapsedIndicator,
  collapsedAttention = false,
  expandedClassName,
}: {
  title: string;
  icon: React.ReactNode;
  tone: 'default' | 'client' | 'internal';
  expanded: React.ReactNode;
  count?: number;
  /**
   * Live content (e.g. a running call): while present and the dropdown is
   * closed, the section auto-expands DOWN as an overlay below its thin
   * strip — only this section, never its siblings.
   */
  live?: React.ReactNode;
  /**
   * Identity of the live items (e.g. active call session ids). The user may
   * collapse the live overlay (they're already in the call / don't want to
   * join); that collapse is deliberately NOT persistent — whenever an id
   * appears here that wasn't present before (a new or parallel call), the
   * overlay expands again. Ids disappearing (a call ending) keep the
   * user's collapsed choice.
   */
  liveIds?: string[];
  /** Stable id for test hooks / aria on the toggle controls. */
  testId?: string;
  /** Increment to open this section from an external navigation target. */
  openSignal?: number;
  /** Notifies consumers when this section's dropdown is opened/closed. */
  onOpenChange?: (open: boolean) => void;
  /** A passive status shown only while the full dropdown is closed. */
  collapsedIndicator?: React.ReactNode;
  /** Visual emphasis for a passive upcoming status; does not change open state. */
  collapsedAttention?: boolean;
  expandedClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [liveCollapsed, setLiveCollapsed] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (openSignal) {
      setOpen(true);
      setLiveCollapsed(false);
      onOpenChange?.(true);
    }
  }, [onOpenChange, openSignal]);

  // Re-expand on any NEW live id (see `liveIds` doc). Layout effect so the
  // reset commits before paint — a call that just arrived never shows a
  // collapsed frame.
  const prevLiveIdsRef = useRef<Set<string>>(new Set());
  useLayoutEffect(() => {
    const seen = prevLiveIdsRef.current;
    const next = new Set(liveIds ?? []);
    const hasNew = [...next].some(id => !seen.has(id));
    next.forEach(id => seen.add(id));
    if (hasNew) setLiveCollapsed(false);
  }, [liveIds]);

  const hasLive = !!live;
  const showLiveOverlay = !open && hasLive && !liveCollapsed;
  // Collapsed live content: the strip itself must clearly say "a call is
  // running under here" since the overlay is gone.
  const collapsedLiveIndicator = !open && hasLive && liveCollapsed;
  const showCollapsedIndicator = !open && !!collapsedIndicator;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Element | null;
      // Radix menus (e.g. the sound picker) render in a portal at the
      // document body — clicks inside them are NOT "outside" this section,
      // otherwise the mousedown closes the panel before the click lands.
      if (target?.closest?.('[data-radix-popper-content-wrapper], [role="dialog"], [role="alertdialog"]')) return;
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        onOpenChange?.(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onOpenChange, open]);

  return (
    <div ref={ref} className="relative flex-1 min-w-0">
      {/* Thin strip: just icon + label height. The strip is a flex row of
          [main toggle button][optional live collapse/expand button] so the
          live toggle is its own accessible control, not a nested button. */}
      <div
        className={cn(
          'w-full rounded-md border transition-colors flex items-stretch text-[11px] uppercase tracking-wide text-gray-400',
          tone === 'client'
            ? 'bg-orange-500/40 border-orange-400 shadow-[0_0_12px_rgba(251,146,60,0.35)]'
            : tone === 'internal'
              ? 'bg-sky-500/15 border-sky-500/40'
              : 'navy-card border-gray-600',
          collapsedLiveIndicator && (tone === 'client' ? 'ring-1 ring-orange-400/80' : 'ring-1 ring-sky-400/70'),
           !open && collapsedAttention && 'border-sky-400/80 bg-sky-500/15 shadow-[0_0_12px_rgba(56,189,248,0.2)]',
        )}
        data-testid={testId ? `${testId}-strip` : undefined}
        data-live-collapsed={collapsedLiveIndicator ? 'true' : undefined}
      >
        <button
          type="button"
          onClick={() => {
            setOpen(o => !o);
            onOpenChange?.(!open);
          }}
          className={cn(
            'flex-1 min-w-0 rounded-md px-2.5 py-1 text-left flex items-center gap-1.5 transition-colors',
            tone === 'client'
              ? 'hover:bg-orange-500/30'
              : tone === 'internal'
                ? 'hover:bg-sky-500/20'
                : 'hover:bg-gray-700/60'
          )}
          aria-expanded={open}
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          {icon}
          <span className="truncate">{title}</span>
          {typeof count === 'number' && count > 0 && (
            <span className={cn(
              'ml-1 px-1.5 rounded-full text-[10px] font-semibold',
              tone === 'client' ? 'bg-orange-400 text-black' : tone === 'internal' ? 'bg-sky-500/30 text-sky-200' : 'bg-gray-600 text-gray-200'
            )}>
              {count}
            </span>
          )}
          {collapsedLiveIndicator && (
            <span
              className={cn(
                'ml-1.5 inline-flex items-center gap-1 text-[10px] font-semibold normal-case tracking-normal',
                tone === 'client' ? 'text-orange-100' : 'text-sky-200'
              )}
              data-testid={testId ? `${testId}-live-indicator` : undefined}
            >
              <span className="relative flex h-2 w-2">
                <span className={cn('animate-ping motion-reduce:animate-none absolute inline-flex h-full w-full rounded-full opacity-75', tone === 'client' ? 'bg-orange-400' : 'bg-sky-400')} />
                <span className={cn('relative inline-flex rounded-full h-2 w-2', tone === 'client' ? 'bg-orange-400' : 'bg-sky-400')} />
              </span>
              Live
            </span>
          )}
          {showCollapsedIndicator && (
            <span
              className="ml-auto min-w-0 truncate text-[10px] font-semibold normal-case tracking-normal tabular-nums text-sky-200"
              data-testid={testId ? `${testId}-schedule-indicator` : undefined}
            >
              {collapsedIndicator}
            </span>
          )}
          <ChevronDown className={cn('w-3 h-3 flex-shrink-0 transition-transform', showCollapsedIndicator ? 'ml-1' : 'ml-auto', open && 'rotate-180')} />
        </button>
        {/* Live overlay collapse/expand — only while there IS live content and
            the full dropdown is closed. Up arrow hides the auto-expanded
            list; when hidden, the same slot offers the re-expand arrow. */}
        {!open && hasLive && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLiveCollapsed(c => !c);
            }}
            className={cn(
              'flex-shrink-0 px-1.5 rounded-r-md border-l flex items-center transition-colors',
              tone === 'client'
                ? 'border-orange-400/60 hover:bg-orange-500/30'
                : tone === 'internal'
                  ? 'border-sky-500/40 hover:bg-sky-500/20'
                  : 'border-gray-600 hover:bg-gray-700/60'
            )}
            aria-label={liveCollapsed ? 'Show live list' : 'Hide live list'}
            title={liveCollapsed ? 'Show live list' : 'Hide live list'}
            aria-expanded={!liveCollapsed}
            aria-controls={testId ? `${testId}-live-overlay` : undefined}
            data-testid={testId ? `${testId}-live-toggle` : undefined}
          >
            {liveCollapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
          </button>
        )}
      </div>
      {/* Full dropdown on click — unchanged behavior. */}
      {open && (
        <div className={cn("absolute left-0 right-0 top-full mt-1 z-[60] rounded-lg border border-gray-600 bg-gray-800 shadow-2xl p-2 max-h-80 overflow-y-auto", expandedClassName)}>
          {expanded}
        </div>
      )}
      {/* Auto-expanding live overlay (only while there IS live content and
          the manual dropdown isn't open). Overlay, so sibling sections and
          the layout below keep their height. */}
      {showLiveOverlay && (
        <div
          id={testId ? `${testId}-live-overlay` : undefined}
          className={cn(
            'absolute left-0 right-0 top-full mt-1 z-[55] rounded-lg border bg-gray-800 shadow-2xl p-1.5 max-h-60 overflow-y-auto',
            tone === 'client' ? 'border-orange-400/70' : tone === 'internal' ? 'border-sky-500/50' : 'border-gray-600'
          )}
        >
          {live}
        </div>
      )}
    </div>
  );
}

export function ActivityBar({ stickyTopPx = 48 }: { stickyTopPx?: number } = {}) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data } = useQuery<{ activeCalls: ActiveCall[]; recentCalls?: RecentCall[]; shares: PreviewShareRow[] }>({
    queryKey: ['/api/activity-bar'],
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });

  const calls = data?.activeCalls ?? [];
  const recentCalls = data?.recentCalls ?? [];
  const hasClientCall = calls.some(isExternalCall);
  const hasInternalCall = calls.some(c => c.kind === 'internal');
  const callTone = hasClientCall ? 'client' : hasInternalCall ? 'internal' : 'default';
  // Stable identity for the live-overlay collapse logic (see Section.liveIds).
  const activeCallIds = useMemo(() => calls.map(c => c.sessionId), [calls]);
  const callActions = useCalendarCallActions();
  const [scheduledViewAs, setScheduledViewAs] = useState<number | 'all' | null>(null);
  // This is the same bounded agenda query used by ScheduledCalendarPanel.
  // React Query shares it by key, so the compact overlay adds no calendar
  // traffic and local clock ticks below never refetch it.
  const calendarRange = useMemo(calendarAgendaRange, []);
  const calendarAgenda = useCalendarAgenda(calendarRange.from, calendarRange.to, scheduledViewAs);
  const calendarNow = useCalendarClock();
  const callbackRequests = useMemo(
    () => (callActions.data?.items ?? []).filter(action => action.status === 'pending' || action.status === 'requested'),
    [callActions.data?.items],
  );
  // The aggregate agenda is deliberately borrowed/read-only. Do not leak
  // this user's callback requests or their action controls into its compact
  // overlay while All is selected.
  const visibleCallbackRequests = scheduledViewAs === 'all' ? [] : callbackRequests;
  const callbackRequestIds = useMemo(() => visibleCallbackRequests.map(action => action.id), [visibleCallbackRequests]);
  const scheduledCalls = useMemo(
    () => (calendarAgenda.data?.items ?? [])
      .filter(item => isUpcomingCalendarCall(item, calendarNow))
      .sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt)),
    [calendarAgenda.data?.items, calendarNow],
  );
  const immediateScheduledCalls = useMemo(
    () => scheduledCalls.filter(item => isPriorityCalendarCall(item, calendarNow)),
    [scheduledCalls, calendarNow],
  );
  const nextScheduledCall = scheduledCalls[0];
  const nextCallWithinFourHours = !!nextScheduledCall
    && Date.parse(nextScheduledCall.startAt) - calendarNow <= 4 * 60 * 60_000;
  const [calendarOpenSignal, setCalendarOpenSignal] = useState(0);
  const [calendarTargetEventId, setCalendarTargetEventId] = useState<string | null>(null);
  const [activeCallsOpenSignal, setActiveCallsOpenSignal] = useState(0);
  const [creatingPulseSessions, setCreatingPulseSessions] = useState<Set<string>>(() => new Set());
  const [callPulseErrors, setCallPulseErrors] = useState<Record<string, string>>({});

  const { joinBySessionId, resurfaceIncomingCall, activeCall, incomingCall, incomingClientCall } = useCall();

  // The ring itself arrives over the websocket, which has no replay: a
  // reconnect gap means the recipient sees this polled row but never the
  // ringing modal (and hears no ringtone). When the server says we are still
  // invited to an internal call and nothing is ringing locally, ask the call
  // context to re-check and resurface the modal. Once per session id — the
  // context re-validates against /api/calls/active, so a declined call can
  // never ring again from a stale poll.
  const resurfacedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!data) return;
    if (activeCall || incomingCall || incomingClientCall) return;
    for (const c of data.activeCalls) {
      if (c.kind !== 'internal' || !isRingingForMe(c)) continue;
      if (resurfacedRef.current.has(c.sessionId)) continue;
      resurfacedRef.current.add(c.sessionId);
      void resurfaceIncomingCall(c.sessionId);
    }
  }, [data, activeCall, incomingCall, incomingClientCall, resurfaceIncomingCall]);

  // Chime when a NEW call appears here (related call — or any call for
  // admins; the server already permission-filters the list). Deliberately
  // NOT played for calls the user is personally part of: their own ring
  // (incoming modal) or ringback (they started the call) already sounds —
  // this tone only covers calls happening "elsewhere". First fetch after
  // mount is the baseline: existing calls never chime retroactively.
  const seenCallIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!data) return;
    const ids = new Set(data.activeCalls.map(c => c.sessionId));
    const seen = seenCallIdsRef.current;
    if (seen === null) {
      seenCallIdsRef.current = ids;
      return;
    }
    // `personal` comes from the SERVER (initiator or invitee row) — the
    // client-side call state (incomingCall etc.) can arrive after the poll
    // and is only kept as a secondary guard.
    const ringingSessions = new Set(
      [activeCall?.session.sessionId, incomingCall?.session.sessionId, incomingClientCall?.sessionId].filter(Boolean)
    );
    const isNew = data.activeCalls.some(
      c => !seen.has(c.sessionId) && !c.personal && !ringingSessions.has(c.sessionId)
    );
    seenCallIdsRef.current = ids;
    if (isNew) audioNotifier.playByType('activeCalls');
  }, [data, activeCall, incomingCall, incomingClientCall]);

  // Callback requests use their own Scheduled sound preference rather than
  // the Active Calls sound. The first response is only a baseline (no
  // retroactive chime), and the identity set is retained so unchanged
  // refetches/reconnect catch-ups can never ring again.
  const seenCallbackIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!callActions.data) return;
    const next = new Set(callbackRequestIds);
    const seen = seenCallbackIdsRef.current;
    if (seen !== null && callbackRequestIds.some(id => !seen.has(id))) {
      audioNotifier.playByType('scheduledCalls');
    }
    if (seen === null) {
      seenCallbackIdsRef.current = next;
    } else {
      next.forEach(id => seen.add(id));
    }
  }, [callActions.data, callbackRequestIds]);

  useEffect(() => {
    const openCalendar = (event: Event) => {
      const eventId = (event as CustomEvent<{ eventId?: string }>).detail?.eventId;
      setCalendarTargetEventId(eventId ? String(eventId) : null);
      setCalendarOpenSignal(value => value + 1);
    };
    window.addEventListener('open-calendar-event', openCalendar);
    window.addEventListener('open-calendar', openCalendar);
    return () => {
      window.removeEventListener('open-calendar-event', openCalendar);
      window.removeEventListener('open-calendar', openCalendar);
    };
  }, []);

  // An opened event id is a navigation instruction, not selected state. The
  // panel consumes it after it has opened the dialog so a later dropdown
  // remount or agenda refetch cannot revive a dialog the user closed.
  const consumeCalendarTarget = useCallback(() => {
    setCalendarTargetEventId(null);
  }, []);

  useEffect(() => {
    const openActiveCalls = () => setActiveCallsOpenSignal(value => value + 1);
    window.addEventListener('open-active-calls', openActiveCalls);
    return () => window.removeEventListener('open-active-calls', openActiveCalls);
  }, []);

  const joinCall = (c: ActiveCall) => {
    if (c.kind === 'client' && c.shareToken) {
      // Client calls run on the share page — same URL the accept flow uses.
      window.open(`/p/${c.shareToken}?call=${encodeURIComponent(c.sessionId)}&nointro`, '_blank', 'noopener');
    } else if (c.kind === 'internal') {
      void joinBySessionId(c.sessionId);
    }
  };

  const createCallPulse = async (sessionId: string) => {
    setCallPulseErrors(errors => {
      const { [sessionId]: _removed, ...rest } = errors;
      return rest;
    });
    setCreatingPulseSessions(sessions => new Set(sessions).add(sessionId));
    try {
      const response = await apiRequest('POST', `/api/calls/${encodeURIComponent(sessionId)}/project-pulse`);
      await response.json();
      // The pulse generator broadcasts normally, but this explicit action
      // must refresh every affected view even if the websocket is unavailable.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['/api/projects'], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['/api/years'], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['/api/activity-bar'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/calendar/timeline'], exact: false }),
      ]);
    } catch (error) {
      setCallPulseErrors(errors => ({
        ...errors,
        [sessionId]: error instanceof Error ? error.message : 'Failed to create call pulse',
      }));
    } finally {
      setCreatingPulseSessions(sessions => {
        const next = new Set(sessions);
        next.delete(sessionId);
        return next;
      });
    }
  };

  const callLabel = (c: ActiveCall) => {
    if (isRingingForMe(c)) {
      const caller = c.initiatorName?.trim() || c.participants?.[0] || 'Someone';
      return `${caller} is calling you`;
    }
    return `${isExternalCall(c) ? 'Client call' : 'Internal call'}`
      + `${c.status === 'pending' ? ' · ringing' : ''}`
      + `${c.participants?.length ? ` · ${c.participants.join(', ')}` : ''}`;
  };

  const callRow = (c: ActiveCall, withJoin: boolean) => {
    const forMe = isRingingForMe(c);
    const label = callLabel(c);
    return (
      <div
        key={c.sessionId}
        className={`flex items-center gap-2 px-1.5 py-1 rounded hover:bg-gray-700/60 min-w-0${forMe ? ' bg-emerald-900/40 ring-1 ring-emerald-500/60' : ''}`}
        data-testid={`activity-call-${c.sessionId}`}
        data-ringing-for-me={forMe ? 'true' : undefined}
      >
        <Thumb src={c.thumbnail} alt={c.projectName ?? ''} />
        <div className="min-w-0 flex-1">
          <div className="text-xs text-gray-200 truncate">
            {c.projectName ?? 'Unknown project'}
            {c.projectId && <span className="text-gray-500 ml-1">{c.projectId}</span>}
          </div>
          <div
            className={`text-[11px] truncate${forMe ? ' font-semibold animate-pulse' : ''}`}
            style={{ color: forMe ? CALL_COLORS.internal : isExternalCall(c) ? CALL_COLORS.external : CALL_COLORS.internal }}
            title={label}
          >
            {label}
          </div>
        </div>
        {forMe && c.kind === 'internal' && !activeCall ? (
          <button
            type="button"
            onClick={() => void resurfaceIncomingCall(c.sessionId)}
            className="px-2 py-1 text-[11px] rounded bg-emerald-600 hover:bg-emerald-500 text-white flex-shrink-0"
            data-testid={`activity-call-answer-${c.sessionId}`}
          >
            Answer
          </button>
        ) : withJoin && (c.kind === 'internal' ? !activeCall : !!c.shareToken) && (
          <button
            type="button"
            onClick={() => joinCall(c)}
            className="px-2 py-1 text-[11px] rounded bg-emerald-600 hover:bg-emerald-500 text-white flex-shrink-0"
          >
            Join
          </button>
        )}
      </div>
    );
  };

  // Past call — greyed to clearly read as history, with duration + who was in.
  const recentCallRow = (c: RecentCall) => {
    const creating = creatingPulseSessions.has(c.sessionId);
    const error = callPulseErrors[c.sessionId];
    return (
      <div key={c.sessionId} className="flex items-center gap-2 px-1.5 py-1 rounded min-w-0 opacity-45">
        <Thumb src={c.thumbnail} alt={c.projectName ?? ''} />
        <div className="min-w-0 flex-1">
          <div className="text-xs text-gray-300 truncate">
            {c.projectName ?? 'Unknown project'}
            {c.projectId && <span className="text-gray-500 ml-1">{c.projectId}</span>}
            <span className="ml-1" style={{ color: isExternalCall(c) ? CALL_COLORS.external : CALL_COLORS.internal }}>· {isExternalCall(c) ? 'Client call' : 'Internal call'}</span>
          </div>
          <div className="text-[11px] text-gray-400 truncate">
            {daysAgo(c.startedAt)}
            {c.durationSec != null ? ` · ${formatDuration(c.durationSec)}` : ''}
            {c.participants.length > 0 ? ` · ${c.participants.join(', ')}` : ''}
          </div>
          {error && (
            <div className="text-[10px] text-red-300 mt-0.5" data-testid={`create-call-pulse-error-${c.sessionId}`}>
              {error}
            </div>
          )}
        </div>
        {c.canCreateCallPulse && (
          <button
            type="button"
            onClick={() => void createCallPulse(c.sessionId)}
            disabled={creating}
            className="px-2 py-1 text-[11px] rounded bg-sky-700 hover:bg-sky-600 disabled:opacity-60 text-white flex-shrink-0"
            data-testid={`create-call-pulse-${c.sessionId}`}
            title={c.isHistoricalCallPulseRecovery
              ? 'Creates a manual historic call record from stored call details. This call was not recorded or transcribed.'
              : 'Create the non-recorded client-share call pulse.'}
          >
            {creating
              ? 'Creating…'
              : c.isHistoricalCallPulseRecovery
                ? 'Create historic call record'
                : 'Create call pulse'}
          </button>
        )}
        <span className="text-[10px] uppercase tracking-wide text-gray-500 flex-shrink-0">Ended</span>
      </div>
    );
  };

  // The thin strip bar is ALWAYS pinned directly under the top filter bar —
  // otherwise it scrolls up and disappears underneath that (sticky) section.
  // z-[45] sits above the sticky column header (z-40) and the timeline date
  // strip (z-30) but below the filter bar (z-50).
  return (
    <div
      className="bg-gray-700 px-6 py-[5px] sticky z-[45] shadow-lg shadow-black/30"
      style={{ top: `${stickyTopPx}px` }}
      data-testid="activity-bar"
    >
      <div className="flex items-stretch gap-3">
        <Section
          title="Active calls"
          icon={<Phone className="w-3 h-3" />}
          tone={callTone}
          count={calls.length}
          testId="active-calls"
          openSignal={activeCallsOpenSignal}
          liveIds={activeCallIds}
          live={
            calls.length > 0 ? (
              <>
                <div className="flex items-center gap-1.5 px-1.5 pb-1 text-[10px] uppercase tracking-wide text-gray-500">
                  <Video className={cn('w-3 h-3', hasClientCall ? 'text-orange-400' : 'text-blue-400')} />
                  Live now
                </div>
                {calls.map(c => callRow(c, true))}
              </>
            ) : undefined
          }
          expanded={
            <>
              {/* Per-user chime picker — HERE (not only in the admin-only
                  Settings dialog) so every team member can set their own
                  tone for new calls appearing in this bar. At the TOP of
                  the dropdown: the body scrolls (max-h-80), so a bottom
                  placement disappears below the fold once history grows. */}
              <div className="flex items-center justify-between px-1.5 pb-1.5 mb-1 border-b border-gray-700">
                <span className="text-[10px] uppercase tracking-wide text-gray-500">New-call sound</span>
                <SoundSelector soundType="soundActiveCalls" label="Sound" />
              </div>
              {calls.length === 0 ? (
                <div className="text-xs text-gray-500 px-1.5 py-2">No active calls right now.</div>
              ) : (
                calls.map(c => callRow(c, true))
              )}
              {recentCalls.length > 0 && (
                <>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 px-1.5 pt-2 pb-1 border-t border-gray-700 mt-1">
                    Recent calls
                  </div>
                  {recentCalls.map(recentCallRow)}
                </>
              )}
            </>
          }
        />
        <Section
          title="Scheduled"
          icon={<CalendarClock className="w-3 h-3" />}
          tone="default"
          count={visibleCallbackRequests.length}
          testId="scheduled-calendar"
          openSignal={calendarOpenSignal}
          liveIds={callbackRequestIds}
          collapsedAttention={nextCallWithinFourHours}
          collapsedIndicator={nextCallWithinFourHours && nextScheduledCall
            ? `Call ${formatCalendarCallCountdown(nextScheduledCall.startAt, calendarNow)}`
            : undefined}
          live={visibleCallbackRequests.length > 0 || immediateScheduledCalls.length > 0 ? (
            <CalendarUrgentList
              actions={visibleCallbackRequests}
              scheduledCalls={immediateScheduledCalls}
              now={calendarNow}
              onSelectCalendarItem={(item) => {
                setCalendarTargetEventId(item.id);
                setCalendarOpenSignal(value => value + 1);
              }}
            />
          ) : undefined}
          expanded={
            <>
              {/* Keep callback-request sounds independent from the chime for
                  calls that are already in progress elsewhere. */}
              <div className="flex items-center justify-between px-1.5 pb-1.5 mb-1 border-b border-gray-700">
                <ScheduledViewAsChips
                  value={scheduledViewAs}
                  onChange={setScheduledViewAs}
                  fallback={<span className="text-[10px] uppercase tracking-wide text-gray-500">New scheduled-call sound</span>}
                />
                <SoundSelector soundType="soundScheduledCalls" label="Sound" />
              </div>
              <ScheduledCalendarPanel
                initialEventId={calendarTargetEventId}
                onInitialEventConsumed={consumeCalendarTarget}
                viewAsUserId={scheduledViewAs}
              />
            </>
          }
        />
        <Section
          title="Latest previews"
          icon={<ImageIcon className="w-3 h-3" />}
          tone="default"
          count={data?.shares.length}
          testId="latest-previews"
          expandedClassName="overflow-hidden"
          expanded={
            <LatestPreviews key={`${user?.id ?? 'signed-out'}:${user?.role ?? ''}`} userId={user?.id ?? null} isAdmin={user?.role === 'admin'} />
          }
        />
      </div>
    </div>
  );
}
